/**
 *
 * Handle functionalities, mapped to the Geo Generator
 * @module geo/functionalities
 * @preferred
 */

/**
 * @preferred
 */

import { Store } from "redux";
import watch from "redux-watch";
import { Observable } from "rxjs/Observable";
import { fromPromise } from "rxjs/observable/fromPromise";
 import isEqual from "lodash.isequal";

import {
  disableFunctionalityCreatorP,
  enableFunctionalityCreatorP
} from "../actions/creators/functionality.creator";
import { Watch } from "../decorators/watch";
import { Service } from "../services";
import { AppStore } from "../store/app";
import { stateOfFunctionality } from "../store/selectors";
import {
  FunctionalityOptions,
  FunctionalityType
} from "../types/functionality";

import { FunctionalityService } from "./services";
import { Watcher } from "../watcher";

/**
 * Abstract class for all functionalities, show all common methods and
 * properties
 *
 */
export interface Functionality extends Watcher {}

@Watch({})
export abstract class Functionality extends FunctionalityType {
  /**
   * @internalapi
   */
  static typeDef: string = "";

  public "@type": string;

  public id: string;

  public name: string;
  
  /**
   * is the functionality selected on ui
   */
  // public selected?: boolean;
  /**
   * is functionality active?
   */
  public active?: boolean;
  /**
   * is function visibile on map
   */
  public visible?: boolean;
  /**
   * a reference to the current store
   */
  public store?: Store<AppStore>;

  // public path: string;

  [id: string]: any;

  protected service?: any;

  public get path(): string {
    return "functionalities." + this.id;
  }

  constructor(options: FunctionalityOptions) {
    super(options);
    if (undefined === options.id) {
      throw new Error("no id in options");
    }
    this.id = options.id;

    if (undefined === options.name) {
      throw new Error("no name in options");
    }
    this.name = options.name;
  }

  /**
   * Is functionality of type of the class
   * @param entry functionalityType to test
   * @param classEntry class (Type)Functionality
   * 
   * @private
   * @internalapi
   */
  public static isOfType(
    func: FunctionalityOptions,
    classFunctionality: any
  ): boolean {
    return classFunctionality.typeDef === func["@type"];
  }

  /**
   * return descriptor of functionality from store
   * @deprecated, see asRessource.
   */
  public asSimpleFunctionality(): FunctionalityOptions {
    if (undefined === this.store) {
      throw new Error("no store defined for functionality");
    }
    return this.store.getState().functionalities[this.id];
  }
  public asRessource(): FunctionalityOptions {
    if (undefined === this.store) {
      throw new Error("no store defined for functionality");
    }
    return this.store.getState().functionalities[this.id];
  }

  /**
   * @private
   */
  public connect(store: Store<AppStore>) {
    const wfunctionality = watch(
      () => stateOfFunctionality(store.getState(), this.id),
      null,
      isEqual
    );
    store.subscribe(
      wfunctionality(
        (newVal: FunctionalityOptions, oldVal: FunctionalityOptions) => {
          Object.assign(this, newVal);
        }
      )
    );

    this.store = store;
  }

  /**
   * Execute the functionality with the given parameters
   * @param parameters object of options for functionality execution. Values
   * depend of functionality type.
   */
  public execute(parameters: any | null = null): Observable<any> {
    if (this.store === undefined) {
      throw new Error("no store defined for functionality");
    }
    return fromPromise(
      enableFunctionalityCreatorP(
        { id: this.id, parameters },
        this.store.dispatch
      )
    );
  }

  /**
   * Disable the functionality
   */
  public disable(options?: any): Observable<any> {
    if (this.store === undefined) {
      throw new Error("no store defined for functionality");
    }
    return fromPromise(
      disableFunctionalityCreatorP(
        { id: this.id, options },
        this.store.dispatch
      )
    );
  }

  /**
   * an handler to access low level functionalities
   */
  public abstract getService():FunctionalityService<Functionality, Service>| void| unknown;
}

/**
 * Hashmap of functionalities, by id
 */
export interface HashFunctionalities {
  [id: string]: Functionality;
}
